import React from 'react';
import { Link } from 'react-router-dom';

import { Field, Form, FormikProps, withFormik } from 'formik';

import { Alert, Button, FormikInput } from '../';
import _ from '../../locale';
import { EmailPasswordAuthSchema } from '../../models/form-validation';
import { styled } from '../../theme';

const StyledAlert = styled(Alert)({
    marginBottom: 20,
});

const ButtonContainer = styled('div')({
    display: 'flex',
    justifyContent: 'flex-end',
});

const ResendLink = styled(Link)(({ theme }) => ({
    cursor: 'pointer',
    color: 'white',
    textDecoration: 'underline',
    fontWeight: theme.typography.fontWeight.bold,
}));

type FormValues = {
    email: string;
    password: string;
};

type Props = {
    signIn: (values: Pick<FormValues, 'email' | 'password'>) => void;
    onSuccess: (response: object) => void;
    resendPath?: string;
};

const mapNameToLabel = (name: string) => _('form_field_' + name);

const InnerForm: React.FC<Props & FormikProps<FormValues>> = (props) => {
    const { isSubmitting, status, resendPath } = props;
    return (
        <Form>
            {status && status.error && <StyledAlert color="error">{status.error}</StyledAlert>}
            {status && status.success && (
                <StyledAlert color="success">{status.success}</StyledAlert>
            )}

            <Field
                type="email"
                name="email"
                component={FormikInput}
                mapNameToLabel={mapNameToLabel}
                required
                autoFocus
                data-private
            />

            <Field
                name="password"
                component={FormikInput}
                mapNameToLabel={mapNameToLabel}
                type="password"
                required
                data-private
            />

            <ButtonContainer>
                <Button color="primary" disabled={isSubmitting} type="submit">
                    {_('sign_in_scene_sign_in_button')}
                </Button>
            </ButtonContainer>
        </Form>
    );
};

export default withFormik<Props, FormValues>({
    validateOnBlur: false,
    validateOnChange: false,
    // initialize values
    mapPropsToValues: () => ({
        email: '',
        password: '',
    }),
    validationSchema: EmailPasswordAuthSchema,

    handleSubmit: (values, form) => {
        const { signIn, onSuccess, resendPath } = form.props;

        signIn(values.email, values.password)
            .then((response) => {
                form.setSubmitting(false);

                onSuccess(response);
            })
            .catch((error) => {
                if (error?.message === 'Account email is not confirmed') {
                    error.message = (
                        <div>
                            Account email is not confirmed.{' '}
                            <ResendLink to={resendPath}>
                                <b>Resend the Client Portal sign up email</b>
                            </ResendLink>{' '}
                            and click the activation link within to continue.
                        </div>
                    );
                }

                form.setStatus({ error: error.message });
                form.setSubmitting(false);
            });
    },
})(InnerForm);
